import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FormCard = ({ 
  form,
  onDuplicate,
  onArchive,
  onShare,
  onPublish,
  isSelected,
  onSelect,
  onMoveToFolder,
  isListView = false,
  // drag-and-drop props
  draggable,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  isDragOver
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const defaultThumbnail = '/assets/images/web-forms-favicon-Green-1770229122289.png';

  const getStatusColor = (status) => {
    return status === 'published' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground';
  };

  const formatDate = (date) => {
    const now = new Date();
    const formDate = new Date(date);
    const diffTime = Math.abs(now - formDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return formDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleDeleteClick = (e) => {
    e?.preventDefault();
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await onArchive(form?.id);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting form:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete Confirmation Modal
  const DeleteModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={(e) => e?.stopPropagation()}>
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => !isDeleting && setShowDeleteModal(false)} />
        <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-md mx-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-semibold text-foreground">Delete Form</h3>
            <button
              onClick={() => !isDeleting && setShowDeleteModal(false)}
              className="text-muted-foreground hover:text-foreground transition-smooth"
              disabled={isDeleting}
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-destructive mt-0.5" />
                <div>
                  <h4 className="font-semibold text-destructive mb-1">Warning: Permanent Action</h4>
                  <p className="text-sm text-muted-foreground">
                    You are about to permanently delete <strong>{form?.title}</strong>. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              All submissions, analytics, and settings associated with this form will also be permanently deleted.
            </p>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                iconName={isDeleting ? "Loader2" : "Trash2"}
                iconPosition="left"
              >
                {isDeleting ? 'Deleting...' : 'Delete Form'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // List View Layout
  if (isListView) {
    return (
      <>
        <div 
          className={`
            relative bg-card border rounded-lg overflow-hidden transition-smooth
            hover:shadow-elevation-md group
            ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'}
            ${isDragOver ? 'border-primary border-dashed bg-primary/5' : ''}
          `}
          draggable={draggable}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDrop={onDrop}
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
        >
          <Link to={`/form-builder?id=${form?.id}`} className="block">
            <div className="flex items-center gap-2.5 px-3 py-2">
              {/* Drag Handle */}
              <div
                className="flex-shrink-0 text-muted-foreground/40 hover:text-muted-foreground cursor-grab active:cursor-grabbing transition-colors"
                title="Drag to reorder"
                onClick={(e) => e?.preventDefault()}
              >
                <Icon name="GripVertical" size={14} />
              </div>

              {/* Checkbox */}
              <div 
                className={`
                  w-4 h-4 rounded border-2 cursor-pointer transition-smooth flex-shrink-0
                  ${isSelected 
                    ? 'bg-primary border-primary' :'bg-card border-border hover:border-primary'
                  }
                `}
                onClick={(e) => {
                  e?.preventDefault();
                  onSelect(form?.id);
                }}
              >
                {isSelected && (
                  <Icon name="Check" size={10} color="white" className="mx-auto mt-0.5" />
                )}
              </div>

              {/* Thumbnail */}
              <div className="relative w-8 h-8 bg-muted overflow-hidden rounded flex-shrink-0">
                <Image
                  src={form?.thumbnail || defaultThumbnail}
                  alt={form?.thumbnailAlt || 'Form thumbnail'}
                  className="w-full h-full object-contain transition-smooth group-hover:scale-105"
                />
              </div>

              {/* Form Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-0.5">
                  <h3 className="text-sm font-heading font-semibold text-foreground line-clamp-1">
                    {form?.title}
                  </h3>
                  <div className={`
                    flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0
                    ${getStatusColor(form?.status)}
                  `}>
                    <Icon 
                      name={form?.status === 'published' ? 'CheckCircle2' : 'Circle'} 
                      size={10} 
                    />
                    <span className="capitalize">{form?.status}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="FileText" size={11} />
                    <span>{form?.type}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Send" size={11} />
                    <span className="whitespace-nowrap">{form?.submissions?.toLocaleString()} submissions</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={10} />
                    <span>Modified {formatDate(form?.lastModified)}</span>
                  </div>
                  {form?.embedded && (
                    <div className="flex items-center gap-1 text-primary">
                      <Icon name="Code2" size={10} />
                      <span>Embedded</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions - Always visible in list view */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  className="h-7 w-7 p-0"
                  onClick={(e) => {
                    e?.preventDefault();
                    window.location.href = `/form-builder?id=${form?.id}`;
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FolderInput"
                  className="h-7 w-7 p-0"
                  onClick={(e) => {
                    e?.preventDefault();
                    onMoveToFolder?.([form?.id]);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Copy"
                  className="h-7 w-7 p-0"
                  onClick={(e) => {
                    e?.preventDefault();
                    onDuplicate(form?.id);
                  }}
                />
                <Button
                  variant={form?.status === 'published' ? 'default' : 'outline'}
                  size="sm"
                  iconName={form?.status === 'published' ? 'Globe' : 'Globe'}
                  className="h-7 w-7 p-0"
                  title={form?.status === 'published' ? 'Unpublish form' : 'Publish form'}
                  onClick={(e) => {
                    e?.preventDefault();
                    onPublish?.(form?.id, form?.status);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Share2"
                  className="h-7 w-7 p-0"
                  onClick={(e) => {
                    e?.preventDefault();
                    onShare(form?.id);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Trash2"
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:border-destructive"
                  onClick={handleDeleteClick}
                />
              </div>
            </div>
          </Link>
        </div>
        <DeleteModal />
      </>
    );
  }

  // Card View Layout (Original)
  return (
    <>
      <div 
        className={`
          relative bg-card border rounded-lg overflow-hidden transition-smooth
          hover:shadow-elevation-md group
          ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'}
        `}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div className="absolute top-3 left-3 z-10">
          <div 
            className={`
              w-5 h-5 rounded border-2 cursor-pointer transition-smooth
              ${isSelected 
                ? 'bg-primary border-primary' :'bg-card border-border hover:border-primary'
              }
            `}
            onClick={(e) => {
              e?.preventDefault();
              onSelect(form?.id);
            }}
          >
            {isSelected && (
              <Icon name="Check" size={12} color="white" className="mx-auto mt-0.5" />
            )}
          </div>
        </div>
        <div className="absolute top-3 right-3 z-10">
          <div className={`
            flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium
            ${getStatusColor(form?.status)}
          `}>
            <Icon 
              name={form?.status === 'published' ? 'CheckCircle2' : 'Circle'} 
              size={12} 
            />
            <span className="capitalize">{form?.status}</span>
          </div>
        </div>
        <Link to={`/form-builder?id=${form?.id}`} className="block">
          <div className="relative w-full h-[115px] md:h-[134px] lg:h-[154px] bg-muted overflow-hidden">
            <Image
              src={form?.thumbnail || defaultThumbnail}
              alt={form?.thumbnailAlt || 'Form thumbnail'}
              className="w-full h-full object-contain transition-smooth group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-smooth" />
          </div>

          <div className="p-4 md:p-5 lg:p-6">
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2 line-clamp-1">
              {form?.title}
            </h3>

            <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Icon name="FileText" size={14} />
                <span>{form?.type}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Icon name="Send" size={14} />
                <span className="whitespace-nowrap">{form?.submissions?.toLocaleString()} submissions</span>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Icon name="Clock" size={12} />
                <span>Modified {formatDate(form?.lastModified)}</span>
              </div>
              {form?.embedded && (
                <div className="flex items-center gap-1.5 text-primary">
                  <Icon name="Code2" size={12} />
                  <span>Embedded</span>
                </div>
              )}
            </div>
          </div>
        </Link>
        <div 
          className={`
            absolute bottom-0 left-0 right-0 p-4 bg-card/95 backdrop-blur-sm
            border-t border-border transition-smooth
            ${showActions ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
          `}
        >
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={(e) => {
                e?.preventDefault();
                window.location.href = `/form-builder?id=${form?.id}`;
              }}
              className="flex-1"
            >
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="FolderInput"
              onClick={(e) => {
                e?.preventDefault();
                onMoveToFolder?.([form?.id]);
              }}
            />
            <Button
              variant="outline"
              size="sm"
              iconName="Copy"
              onClick={(e) => {
                e?.preventDefault();
                onDuplicate(form?.id);
              }}
            />
            <Button
              variant={form?.status === 'published' ? 'default' : 'outline'}
              size="sm"
              iconName="Globe"
              title={form?.status === 'published' ? 'Unpublish form' : 'Publish form'}
              onClick={(e) => {
                e?.preventDefault();
                onPublish?.(form?.id, form?.status);
              }}
            />
            <Button
              variant="outline"
              size="sm"
              iconName="Share2"
              onClick={(e) => {
                e?.preventDefault();
                onShare(form?.id);
              }}
            />
            <Button
              variant="outline"
              size="sm"
              iconName="Trash2"
              onClick={handleDeleteClick}
              className="text-destructive hover:text-destructive hover:border-destructive"
            />
          </div>
        </div>
      </div>
      <DeleteModal />
    </>
  );
};

export default FormCard;